import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    IconButton,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Close as CloseIcon,
    Email as EmailIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import swal from 'sweetalert';
import { EnviarCorreopdf } from '../FunctionLogin';
import { listFiles, createFolder, uploadFile, uploadBase64 } from '../../services/docsService';
import { Save as SaveIcon, GetApp as GetAppIcon } from '@mui/icons-material';

const GlassDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiBackdrop-root': {
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    '& .MuiDialog-paper': {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
        maxWidth: '90vw',
        maxHeight: '90vh',
    },
}));

const GlassDialogTitle = styled(DialogTitle)(({ theme }) => ({
    backgroundColor: 'rgba(25, 118, 210, 0.1)',
    borderBottom: '1px solid rgba(25, 118, 210, 0.2)',
    padding: theme.spacing(2, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}));

const GlassDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    minHeight: '70vh',
}));

const GlassDialogActions = styled(DialogActions)(({ theme }) => ({
    backgroundColor: 'rgba(25, 118, 210, 0.05)',
    borderTop: '1px solid rgba(25, 118, 210, 0.1)',
    padding: theme.spacing(2, 3),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: '50px',
    padding: '10px 24px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '1rem',
    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px 0 rgba(0,0,0,0.15)',
    },
}));

export default function PDFPreviewDialog({ open, pdfUrl, onClose, title = "Evaluación ", supplierName = "", evaluationType = "", hideSaveButton = false, showDownloadButton = true }) {

    // Helper to get ID from Google Drive URL (Reusable)
    const getGoogleDriveId = (url) => {
        if (!url) return null;
        const viewMatch = url.match(/\/file\/d\/([^\/]+)/);
        if (viewMatch) return viewMatch[1];
        const openMatch = url.match(/[?&]id=([^&]+)/);
        if (openMatch) return openMatch[1];
        return null;
    };

    // Función para convertir URLs de Google Drive al formato de vista previa
    const getEmbedUrl = (url) => {
        if (!url) return '';

        console.log("📄 PDF URL original:", url);

        // Si es Google Cloud Storage, devolver tal cual (se puede embeber directamente)
        if (url.includes('storage.googleapis.com')) {
            console.log("✅ URL de Google Cloud Storage - no requiere conversión");
            return url;
        }

        // Si ya es una URL de preview, devolverla tal cual
        if (url.includes('/preview')) {
            console.log("✅ URL ya tiene /preview");
            return url;
        }

        const fileId = getGoogleDriveId(url);

        // Si encontramos un ID, convertir a formato de preview
        if (fileId) {
            const embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
            console.log("🔄 URL convertida a:", embedUrl);
            return embedUrl;
        }

        console.log("⚠️ No se pudo extraer ID, devolviendo URL original");
        // Si no es Google Drive, devolver la URL original
        return url;
    };

    // Helper to convert URL to Base64
    const urlToBase64 = async (url) => {
        try {
            let fetchUrl = url;
            // Convert to download URL if it's Google Drive to likely avoid preview HTML wrapper
            const fileId = getGoogleDriveId(url);
            if (fileId) {
                fetchUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
            }

            console.log("⬇️ Intentando descargar PDF desde:", fetchUrl);

            // Try direct fetch first
            let response;
            try {
                response = await fetch(fetchUrl);
                if (!response.ok) throw new Error("Direct fetch failed");
            } catch (directError) {
                console.warn("⚠️ Falló descarga directa (CORS probable). Intentando con proxy...");
                // Fallback to CORS proxy (using allorigins.win as it's a stable public proxy)
                // Note: In a real production app, this should be your own backend proxy.
                const proxyUrl = "https://api.allorigins.win/raw?url=" + encodeURIComponent(fetchUrl);
                try {
                    response = await fetch(proxyUrl);
                } catch (proxyError) {
                    // If proxy fails too, throw original error or generic
                    throw new Error("Failed to fetch from URL and Proxy.");
                }
            }

            if (!response || !response.ok) throw new Error(`HTTP error! status: ${response ? response.status : 'unknown'}`);
            const blob = await response.blob();

            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const res = reader.result.split(',')[1];
                    if (res) resolve(res);
                    else reject(new Error("Failed to convert blob to base64"));
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error("❌ Error fetching PDF from URL:", error);
            throw error;
        }
    };

    const handleSaveToDocs = async () => {
        if (!pdfUrl) {
            swal("Error", "No existe URL del PDF para guardar.", "error");
            return;
        }

        const proveedorId = localStorage.getItem('Proveedor');
        if (!proveedorId) {
            swal("Error", "No Provider ID found in session.", "error");
            return;
        }

        try {
            swal({
                title: "Guardando...",
                text: "Descargando PDF desde URL... Por favor espere.",
                icon: "info",
                buttons: false,
                closeOnClickOutside: false,
                closeOnEsc: false,
            });

            // --- 1. Get Base64 Content ---
            console.log("🌐 Intentando obtener Base64 desde URL:", pdfUrl);
            const finalBase64 = await urlToBase64(pdfUrl);

            if (!finalBase64) {
                throw new Error("No se pudo obtener el contenido Base64 del PDF.");
            }

            // --- 2. Prepare Paths ---
            const userRoot = proveedorId + "/";
            const evalFolder = userRoot + "Evaluaciones/";
            const targetFolder = evalFolder + "Evaluaciones 2026/";

            // Filename generation
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
            const safeTitle = (title || "Evaluacion").replace(/[^a-zA-Z0-9]/g, "_");
            const safeSupplier = (supplierName || "").replace(/[^a-zA-Z0-9]/g, "_");
            const fileName = `${safeTitle}_${safeSupplier}_${dateStr}_${timeStr}.pdf`;

            // --- 3. Ensure Folders Exist ---
            try {
                const evalData = await listFiles(evalFolder);
                if (evalData && !evalData.carpetas) await createFolder(evalFolder);
            } catch (e) { await createFolder(evalFolder); }

            try {
                const targetData = await listFiles(targetFolder);
                if (targetData && !targetData.carpetas) await createFolder(targetFolder);
            } catch (e) { await createFolder(targetFolder); }

            // --- 4. Upload ---
            swal({
                title: "Subiendo...",
                text: "Guardando en el servidor...",
                icon: "info",
                buttons: false,
            });
            await uploadBase64(targetFolder, fileName, finalBase64);

            swal.close();
            swal("Guardado", `Se guardó correctamente.\nUbicación: Evaluaciones > Evaluaciones 2026\nArchivo: ${fileName}`, "success");

        } catch (error) {
            console.error("Error saving to docs:", error);
            swal.close();
            swal("Error", "No se pudo guardar el archivo.\n" + (error.message || error), "error");
        }
    };

    const handleDownload = () => {
        if (!pdfUrl) return;

        // Use the same fallback URL logic we have in urlToBase64
        let downloadUrl = pdfUrl;
        const fileId = getGoogleDriveId(pdfUrl);
        if (fileId) {
            downloadUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
        }

        window.open(downloadUrl, '_blank');
    };

    if (!pdfUrl) return null;

    const embedUrl = getEmbedUrl(pdfUrl);
    console.log("🎯 URL final para iframe:", embedUrl);

    return (
        <GlassDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    height: '85vh',
                }
            }}
        >
            <GlassDialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VisibilityIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                        {title}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </GlassDialogTitle>

            <GlassDialogContent>
                <Box
                    sx={{
                        flex: 1,
                        border: '2px solid #e0e0e0',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    }}
                >
                    <iframe
                        src={embedUrl}
                        title="PDF Preview"
                        width="100%"
                        height="100%"
                        style={{
                            border: 'none',
                            minHeight: '70vh',
                        }}
                    />
                </Box>
            </GlassDialogContent>

            <GlassDialogActions>
                <StyledButton
                    onClick={onClose}
                    variant="outlined"
                    color="inherit"
                >
                    Cerrar
                </StyledButton>
                {!hideSaveButton && (
                    <StyledButton
                        onClick={handleSaveToDocs}
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        sx={{ ml: 2, bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d47a1' } }}
                    >
                        Guardar en Documentación
                    </StyledButton>
                )}
                {showDownloadButton && (
                    <StyledButton
                        onClick={handleDownload}
                        variant="contained"
                        color="primary"
                        startIcon={<GetAppIcon />}
                        sx={{ ml: 2, bgcolor: '#BD1F34', '&:hover': { bgcolor: '#a01a2c' } }}
                    >
                        Descargar PDF
                    </StyledButton>
                )}
            </GlassDialogActions>
        </GlassDialog >
    );
}
